import { Box, keyframes } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _dark = clientThemePalette[CLIENT_THEME].dark;
const _light = clientThemePalette[CLIENT_THEME].light;

// Keyframe animations
export const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled animated button wrapper
export const ButtonWrapper = styled('div')<{ isExpanded: boolean }>(
  ({ isExpanded }) => ({
    position: isExpanded ? 'relative' : 'absolute',
    bottom: isExpanded ? 'none' : '30%',
    left: isExpanded ? 'none' : '50%',
    transform: isExpanded ? 'none' : 'translate(-50%, 0)',
    transformOrigin: 'center center',
    cursor: 'pointer',
    marginLeft: isExpanded ? '20%' : '0',
  })
);

// Styled left section
export const LeftSection = styled(Box)<{
  isExpanded: boolean;
  enableDarkTheme: boolean;
}>(({ isExpanded, enableDarkTheme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  margin: '10px',
  width: isExpanded ? '20%' : '100%',
  height: '100%',
  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: isExpanded ? 'scroll' : 'hidden',
  zIndex: isExpanded ? 2 : 1,
  backgroundColor: isExpanded
    ? enableDarkTheme
      ? alpha(_dark.pageBackground, 0.5)
      : alpha(_light.pageBackground, 0.2)
    : 'none',
  borderRadius: '10px',
}));

// Styled right section
export const RightSection = styled(Box)<{ isExpanded: boolean }>(
  ({ isExpanded }) => ({
    position: 'absolute',
    right: 0,
    top: 0,
    width: isExpanded ? '80%' : '0%',
    height: '100%',
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    opacity: isExpanded ? 1 : 0,
    zIndex: isExpanded ? 1 : 0,
  })
);

// Styled content container
export const ContentContainer = styled(Box)<{ isExpanded: boolean }>(
  ({ isExpanded }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    padding: '20px',
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isExpanded
      ? 'translateX(-10%) scale(0.8)'
      : 'translateX(0) scale(1)',
    transformOrigin: 'center center',
  })
);

// Styled grid box
export const GridBox = styled(Box)<{
  enableDarkTheme: boolean;
  isSelected?: boolean;
}>(({ enableDarkTheme, isSelected }) => ({
  background: enableDarkTheme
    ? `linear-gradient(135deg, ${_dark.boxBackground} 0%, ${_dark.toolBarBackground} 100%)`
    : `linear-gradient(135deg, ${_light.pageBackground} 0%, #ffffff 100%)`,
  borderRadius: '20px',
  padding: '28px',
  height: '140px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: enableDarkTheme
    ? '2px solid rgba(255, 255, 255, 0.3)'
    : '2px solid rgba(100, 100, 100, 0.3)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'visible',
  color: enableDarkTheme ? 'white' : 'black',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    borderRadius: '20px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '12px solid transparent',
    borderRight: '12px solid transparent',
    borderTop: isSelected
      ? `12px solid ${_dark.primary}`
      : '12px solid transparent',
    transition: 'all 0.3s ease',
    opacity: isSelected ? 1 : 0,
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
    background: `linear-gradient(135deg, ${_dark.primary} 0%, ${_dark.primaryDark} 100%)`,
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
  ...(isSelected && {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
    background: `linear-gradient(135deg, ${_dark.primary} 0%, ${_dark.primaryDark} 100%)`,
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.8)',
    '&::before': {
      opacity: 1,
    },
  }),
  animation: `${fadeIn} 0.6s ease-out forwards`,
  '&:nth-of-type(1)': { animationDelay: '0.1s' },
  '&:nth-of-type(2)': { animationDelay: '0.2s' },
  '&:nth-of-type(3)': { animationDelay: '0.3s' },
  '&:nth-of-type(4)': { animationDelay: '0.4s' },
}));

// Styled chart box for dynamic chart grid
export const ChartBox = styled(Box)(
  ({ enableDarkTheme }: { enableDarkTheme: boolean }) => ({
    background: enableDarkTheme
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)'
      : 'linear-gradient(135deg, rgba(240, 248, 255, 0.9) 0%, rgba(245, 245, 245, 0.8) 100%)',
    borderRadius: '12px',
    padding: '16px',
    height: '320px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    boxShadow: enableDarkTheme
      ? '0 4px 20px rgba(0, 0, 0, 0.2)'
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${
      enableDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(200, 200, 200, 0.3)'
    }`,
    backdropFilter: 'blur(8px)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: enableDarkTheme
        ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      borderRadius: '12px',
    },
    '&:hover': {
      transform: 'translateY(-6px) scale(1.02)',
      boxShadow: enableDarkTheme
        ? '0 8px 30px rgba(0, 0, 0, 0.3)'
        : '0 8px 30px rgba(0, 0, 0, 0.15)',
      background: enableDarkTheme
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.9) 100%)',
      border: `1px solid ${
        enableDarkTheme
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(180, 180, 180, 0.5)'
      }`,
      '&::before': {
        opacity: 1,
      },
    },
    animation: `${fadeIn} 0.8s ease-out forwards`,
  })
);
